/**
 * Calendar
 *
 * Calendário com seleção de data única ou intervalo.
 * Baseado no layout de Calender.md, estilizado via Calendar.css.
 */

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ChevronLeft,
  ChevronRight,
  X,
  Calendar as CalendarIcon,
  Check,
} from 'lucide-react';
import { parseDateOnly, toDateInputValue } from '../../../utils/dateUtils';
import './Calendar.css';

const MONTH_NAMES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

const DAYS_OF_WEEK = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const stripTime = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const isSameDay = (d1, d2) => {
  if (!d1 || !d2) return false;
  return (
    d1.getFullYear() === d2.getFullYear()
    && d1.getMonth() === d2.getMonth()
    && d1.getDate() === d2.getDate()
  );
};

const isToday = (date) => isSameDay(date, new Date());

const isBetween = (date, start, end) => {
  if (!start || !end) return false;
  const d = stripTime(date).getTime();
  const s = stripTime(start).getTime();
  const e = stripTime(end).getTime();
  const min = Math.min(s, e);
  const max = Math.max(s, e);
  return d > min && d < max;
};

const Calendar = ({
  mode = 'range',
  onRangeSelect,
  onDateSelect,
  initialStartDate = null,
  initialEndDate = null,
  initialDate = null,
  minDate = null,
  maxDate = null,
  className = '',
  compact = false,
  title = 'Período',
  subtitle = 'Início e Fim',
}) => {
  const resolvedInitial = initialDate || initialStartDate;

  const [startDate, setStartDate] = useState(() => (
    resolvedInitial ? parseDateOnly(resolvedInitial) : null
  ));
  const [endDate, setEndDate] = useState(() => (
    mode === 'range' && initialEndDate ? parseDateOnly(initialEndDate) : null
  ));
  const [hoveredDate, setHoveredDate] = useState(null);
  const [viewMode, setViewMode] = useState('days');
  const [navYear, setNavYear] = useState(() => {
    if (resolvedInitial) return parseDateOnly(resolvedInitial)?.getFullYear() || new Date().getFullYear();
    return new Date().getFullYear();
  });
  const [currentDate, setCurrentDate] = useState(() => (
    resolvedInitial ? parseDateOnly(resolvedInitial) || new Date() : new Date()
  ));

  useEffect(() => {
    const next = initialDate || initialStartDate;
    setStartDate(next ? parseDateOnly(next) : null);
    setEndDate(mode === 'range' && initialEndDate ? parseDateOnly(initialEndDate) : null);
    if (next) {
      const parsed = parseDateOnly(next);
      if (parsed) {
        setCurrentDate(parsed);
        setNavYear(parsed.getFullYear());
      }
    }
  }, [initialDate, initialStartDate, initialEndDate, mode]);

  const minDateObj = minDate ? parseDateOnly(minDate) : null;
  const maxDateObj = maxDate ? parseDateOnly(maxDate) : null;

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const isDisabled = (date) => {
    const stripped = stripTime(date);
    if (minDateObj && stripped.getTime() < stripTime(minDateObj).getTime()) return true;
    if (maxDateObj && stripped.getTime() > stripTime(maxDateObj).getTime()) return true;
    return false;
  };

  const changeMonth = (offset) => {
    setCurrentDate(new Date(year, month + offset, 1));
  };

  const calendarDays = useMemo(() => {
    const firstDayIndex = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const daysInPrevMonth = new Date(year, month, 0).getDate();
    const days = [];

    for (let i = firstDayIndex - 1; i >= 0; i -= 1) {
      days.push({
        date: new Date(year, month - 1, daysInPrevMonth - i),
        isCurrentMonth: false,
        dayNum: daysInPrevMonth - i,
      });
    }

    for (let i = 1; i <= daysInMonth; i += 1) {
      days.push({
        date: new Date(year, month, i),
        isCurrentMonth: true,
        dayNum: i,
      });
    }

    const remainingCells = 42 - days.length;
    for (let i = 1; i <= remainingCells; i += 1) {
      days.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false,
        dayNum: i,
      });
    }

    return days;
  }, [year, month]);

  const yearsRef = useRef(null);
  const yearsList = useMemo(() => {
    const currentY = new Date().getFullYear();
    const minParsed = minDate ? parseDateOnly(minDate) : null;
    const maxParsed = maxDate ? parseDateOnly(maxDate) : null;
    const minY = minParsed ? minParsed.getFullYear() : currentY - 120;
    const maxY = maxParsed ? maxParsed.getFullYear() : currentY + 8;
    const start = Math.min(minY, maxY);
    const end = Math.max(minY, maxY);
    const list = [];
    for (let i = start; i <= end; i += 1) list.push(i);
    return list;
  }, [minDate, maxDate]);

  const handleDateClick = (date) => {
    if (isDisabled(date)) return;

    if (mode === 'single') {
      setStartDate(date);
      setEndDate(null);
      onDateSelect?.(date);
      if (onRangeSelect) onRangeSelect(date, null);
      return;
    }

    let newStart = null;
    let newEnd = null;

    if (!startDate || (startDate && endDate)) {
      newStart = date;
      newEnd = null;
      setStartDate(date);
      setEndDate(null);
    } else if (date.getTime() < startDate.getTime()) {
      newStart = date;
      newEnd = null;
      setStartDate(date);
      setEndDate(null);
    } else {
      newStart = startDate;
      newEnd = date;
      setEndDate(date);
    }

    onRangeSelect?.(newStart, newEnd);
  };

  const handleReset = () => {
    setStartDate(null);
    setEndDate(null);
    setHoveredDate(null);
    onRangeSelect?.(null, null);
    onDateSelect?.(null);
  };

  useLayoutEffect(() => {
    if (viewMode !== 'years' || !yearsRef.current) return;
    const current = yearsRef.current.querySelector('.is-current');
    if (!current) return;
    yearsRef.current.scrollTop = current.offsetTop - (yearsRef.current.clientHeight / 2) + (current.clientHeight / 2);
  }, [viewMode, year]);

  const scrollYears = (direction) => {
    yearsRef.current?.scrollBy({ top: direction * 160, behavior: 'smooth' });
  };

  const handleYearSelect = (selectedYear) => {
    setNavYear(selectedYear);
    setViewMode('months');
  };

  const handleMonthSelect = (selectedMonthIndex) => {
    setCurrentDate(new Date(navYear, selectedMonthIndex, 1));
    setViewMode('days');
  };

  const getBorderRadiusClass = (cell, idx, flags) => {
    const { isStart, isEnd, inRange, inHoverRange, isCellHovered } = flags;
    const isFirstInLine = idx % 7 === 0;
    const isLastInLine = idx % 7 === 6;

    if (mode === 'single') return 'rounded-full';

    const rangeLeftRounded = inRange && (
      isFirstInLine
      || isSameDay(new Date(cell.date.getFullYear(), cell.date.getMonth(), cell.date.getDate() - 1), startDate)
    );
    const rangeRightRounded = inRange && (
      isLastInLine
      || isSameDay(new Date(cell.date.getFullYear(), cell.date.getMonth(), cell.date.getDate() + 1), endDate)
    );
    const hoverLeftRounded = inHoverRange && isFirstInLine;
    const hoverRightRounded = inHoverRange && (isLastInLine || isCellHovered);

    if (isStart) return endDate ? 'rounded-left' : 'rounded-full';
    if (isEnd) return 'rounded-right';
    if (rangeLeftRounded && rangeRightRounded) return 'rounded-full';
    if (rangeLeftRounded) return 'rounded-left';
    if (rangeRightRounded) return 'rounded-right';
    if (hoverLeftRounded && hoverRightRounded) return 'rounded-full';
    if (hoverLeftRounded) return 'rounded-left';
    if (hoverRightRounded) return 'rounded-right';
    if (inRange || inHoverRange) return '';
    return 'rounded-full';
  };

  const singleLabel = startDate
    ? startDate.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : 'Selecione';

  return (
    <div className={`erp-calendar${compact ? ' is-compact' : ''}${startDate || endDate ? ' has-selection' : ''} ${className}`.trim()}>
      {(startDate || endDate) && (
        <button
          type="button"
          onClick={handleReset}
          className="erp-calendar-reset"
          aria-label="Limpar data"
          title="Limpar data"
        >
          <X size={18} strokeWidth={2.6} />
        </button>
      )}
      <div className="erp-calendar-header">
        <div className="erp-calendar-header-info">
          <div className="erp-calendar-icon">
            <CalendarIcon size={18} />
          </div>
          <div>
            <h3 className="erp-calendar-title">{title}</h3>
            <p className="erp-calendar-subtitle">
              {mode === 'single' ? 'Data de vencimento' : subtitle}
            </p>
          </div>
        </div>

        <div className="erp-calendar-selection">
          {mode === 'single' ? (
            <div className="erp-calendar-selection-item">
              <span className={startDate ? 'is-active' : 'is-empty'}>{singleLabel}</span>
            </div>
          ) : (
            <>
              <div className="erp-calendar-selection-item">
                <span className={startDate ? 'is-active' : 'is-empty'}>
                  {startDate
                    ? startDate.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
                    : 'Início'}
                </span>
              </div>
              <div className="erp-calendar-selection-divider" />
              <div className="erp-calendar-selection-item">
                <span className={endDate ? 'is-active' : 'is-empty'}>
                  {endDate
                    ? endDate.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
                    : 'Fim'}
                </span>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="erp-calendar-nav">
        <button
          type="button"
          className="erp-calendar-nav-btn"
          onClick={() => {
            if (viewMode === 'days') changeMonth(-1);
            else if (viewMode === 'months') setNavYear((prev) => prev - 1);
            else scrollYears(-1);
          }}
        >
          <ChevronLeft size={16} />
        </button>

        <button
          type="button"
          className="erp-calendar-nav-label"
          onClick={() => setViewMode(viewMode === 'days' ? 'years' : 'days')}
        >
          {viewMode === 'days' && `${MONTH_NAMES[month]} ${year}`}
          {viewMode === 'years' && 'Selecione o Ano'}
          {viewMode === 'months' && `Meses de ${navYear}`}
        </button>

        <button
          type="button"
          className="erp-calendar-nav-btn"
          onClick={() => {
            if (viewMode === 'days') changeMonth(1);
            else if (viewMode === 'months') setNavYear((prev) => prev + 1);
            else scrollYears(1);
          }}
        >
          <ChevronRight size={16} />
        </button>
      </div>

      <div className="erp-calendar-body">
        {viewMode === 'days' && (
          <div className="erp-calendar-grid">
            {DAYS_OF_WEEK.map((day) => (
              <span key={day} className="erp-calendar-weekday">{day}</span>
            ))}

            {calendarDays.map((cell, idx) => {
              const isStart = isSameDay(cell.date, startDate);
              const isEnd = mode === 'range' && isSameDay(cell.date, endDate);
              const inRange = mode === 'range' && isBetween(cell.date, startDate, endDate);
              const isTodayDate = isToday(cell.date);
              const disabled = isDisabled(cell.date);

              const inHoverRange = mode === 'range'
                && startDate
                && !endDate
                && hoveredDate
                && cell.date.getTime() > startDate.getTime()
                && cell.date.getTime() <= hoveredDate.getTime();

              const isCellHovered = isSameDay(cell.date, hoveredDate);

              const classes = ['erp-calendar-day'];
              if (!cell.isCurrentMonth) classes.push('is-other-month');
              if (disabled) classes.push('is-disabled');
              if (mode === 'single' && isStart) classes.push('is-selected-single');
              if (mode === 'range' && isStart) classes.push('is-selected-start');
              if (isEnd) classes.push('is-selected-end');
              if (inRange) classes.push('is-in-range');
              if (inHoverRange) classes.push('is-in-hover-range');
              classes.push(getBorderRadiusClass(cell, idx, {
                isStart,
                isEnd,
                inRange,
                inHoverRange,
                isCellHovered,
              }));

              return (
                <button
                  key={`${cell.date.toISOString()}-${idx}`}
                  type="button"
                  className={classes.join(' ')}
                  disabled={disabled}
                  onClick={() => handleDateClick(cell.date)}
                  onMouseEnter={() => setHoveredDate(cell.date)}
                  onMouseLeave={() => setHoveredDate(null)}
                >
                  <div className="erp-calendar-day-content">
                    <span>{cell.dayNum}</span>
                    {isTodayDate && <span className="erp-calendar-day-dot" />}
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {viewMode === 'years' && (
          <div className="erp-calendar-years" ref={yearsRef}>
            {yearsList.map((yItem) => (
              <button
                key={yItem}
                type="button"
                className={`erp-calendar-picker-btn${yItem === year ? ' is-current' : ''}`}
                onClick={() => handleYearSelect(yItem)}
              >
                {yItem}
              </button>
            ))}
          </div>
        )}

        {viewMode === 'months' && (
          <div className="erp-calendar-months">
            {MONTH_NAMES.map((mName, mIdx) => (
              <button
                key={mName}
                type="button"
                className={`erp-calendar-picker-btn${mIdx === month && navYear === year ? ' is-current' : ''}`}
                onClick={() => handleMonthSelect(mIdx)}
              >
                {mName.substring(0, 3)}
              </button>
            ))}
          </div>
        )}
      </div>

      {mode === 'range' && startDate && endDate && (
        <div className="erp-calendar-footer">
          <Check size={12} />
          <span>
            {`Range de ${Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 3600 * 24)) + 1} dias`}
          </span>
        </div>
      )}

      {mode === 'single' && startDate && (
        <div className="erp-calendar-footer">
          <Check size={12} />
          <span>Data selecionada</span>
        </div>
      )}
    </div>
  );
};

export const CalendarDatePicker = ({
  value,
  onChange,
  minDate,
  maxDate,
  placeholder = 'Selecionar data',
  title = 'Vencimento',
  subtitle,
  className = '',
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [narrow, setNarrow] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 320, maxHeight: 0 });
  const triggerRef = useRef(null);
  const selected = value ? parseDateOnly(value) : null;
  const displayValue = selected
    ? selected.toLocaleDateString('pt-BR')
    : placeholder;

  useEffect(() => {
    const media = window.matchMedia('(max-width: 768px)');
    const apply = () => setNarrow(media.matches);
    apply();
    media.addEventListener('change', apply);
    return () => media.removeEventListener('change', apply);
  }, []);

  const updatePosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const gap = 8;
    const dropdown = window.matchMedia('(max-width: 768px)').matches;
    const width = dropdown
      ? Math.min(Math.max(rect.width, 280), window.innerWidth - 16)
      : Math.min(320, window.innerWidth - 16);
    const estimatedHeight = dropdown ? Math.min(380, window.innerHeight - 16) : 420;

    let top = rect.bottom + gap;
    let left = rect.left;

    if (top + estimatedHeight > window.innerHeight - gap) {
      top = Math.max(gap, rect.top - estimatedHeight - gap);
    }

    if (left + width > window.innerWidth - gap) {
      left = window.innerWidth - width - gap;
    }
    if (left < gap) left = gap;

    const maxHeight = dropdown
      ? Math.max(240, Math.min(estimatedHeight, window.innerHeight - top - gap))
      : 0;
    setPosition({ top, left, width, maxHeight });
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePosition();

    const handleReposition = () => updatePosition();
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);

    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return undefined;

    const handleEscape = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open]);

  const portalContent = open ? (
    <>
      <div
        className="calendar-date-picker-backdrop"
        onClick={() => setOpen(false)}
        aria-hidden
      />
      <div
        className={`calendar-date-picker-popover${narrow ? ' is-dropdown' : ''}`}
        style={{
          top: `${position.top}px`,
          left: `${position.left}px`,
          width: `${position.width}px`,
          ...(narrow && position.maxHeight ? { maxHeight: `${position.maxHeight}px` } : {}),
        }}
        role="dialog"
        aria-modal="true"
        aria-label="Selecionar data"
      >
        <Calendar
          mode="single"
          compact
          initialDate={value}
          minDate={minDate}
          maxDate={maxDate}
          title={title}
          subtitle={subtitle}
          onDateSelect={(date) => {
            onChange?.(date ? toDateInputValue(date) : '');
            setOpen(false);
          }}
        />
      </div>
    </>
  ) : null;

  return (
    <div className={`calendar-date-picker ${className}`.trim()}>
      <button
        ref={triggerRef}
        type="button"
        className={`calendar-date-picker-trigger${selected ? '' : ' is-empty'}`}
        onClick={() => !disabled && setOpen((prev) => !prev)}
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <span>{displayValue}</span>
        <CalendarIcon size={14} />
      </button>

      {portalContent && createPortal(portalContent, document.body)}
    </div>
  );
};

/**
 * Calendário flutuante (portal) para seleção de intervalo — ancorado a um botão externo.
 */
export const CalendarRangePicker = ({
  open,
  onOpenChange,
  anchorRef,
  startValue,
  endValue,
  onRangeChange,
  onClear,
  selectSingleDay = false,
  minDate,
  maxDate,
  title = 'Período',
  subtitle = 'Início e Fim',
}) => {
  const [position, setPosition] = useState({ top: 0, left: 0, width: 320 });
  const [pendingStart, setPendingStart] = useState(null);
  const [pendingEnd, setPendingEnd] = useState(null);

  const updatePosition = useCallback(() => {
    const el = anchorRef?.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const width = Math.min(340, window.innerWidth - 16);
    const estimatedHeight = 480;
    const gap = 8;

    let top = rect.bottom + gap;
    let left = rect.right - width;

    if (top + estimatedHeight > window.innerHeight - gap) {
      top = Math.max(gap, rect.top - estimatedHeight - gap);
    }

    if (left + width > window.innerWidth - gap) {
      left = window.innerWidth - width - gap;
    }
    if (left < gap) left = gap;

    setPosition({ top, left, width });
  }, [anchorRef]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePosition();

    const handleReposition = () => updatePosition();
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);

    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return undefined;

    const handleEscape = (event) => {
      if (event.key === 'Escape') onOpenChange?.(false);
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onOpenChange]);

  useEffect(() => {
    if (!open) return;
    setPendingStart(startValue || null);
    setPendingEnd(endValue || null);
  }, [open, startValue, endValue]);

  const handleRangeSelect = (start, end) => {
    const startValue = start ? toDateInputValue(start) : null;
    const endValue = end ? toDateInputValue(end) : (selectSingleDay ? startValue : null);
    setPendingStart(startValue);
    setPendingEnd(endValue);

    if (selectSingleDay && startValue) {
      onRangeChange?.(startValue, endValue || startValue);
      if (start && end && startValue !== endValue) onOpenChange?.(false);
      return;
    }

    if (start && end) {
      onRangeChange?.(startValue, endValue);
      onOpenChange?.(false);
    }
  };

  const handleApply = () => {
    const startValue = pendingStart;
    const endValue = pendingEnd || (selectSingleDay ? pendingStart : null);
    if (!startValue || !endValue) return;
    onRangeChange?.(startValue, endValue);
    onOpenChange?.(false);
  };

  const handleClear = () => {
    setPendingStart(null);
    setPendingEnd(null);
    onClear?.();
    onOpenChange?.(false);
  };

  if (!open) return null;

  const portalContent = (
    <>
      <div
        className="calendar-date-picker-backdrop"
        onClick={() => onOpenChange?.(false)}
        aria-hidden
      />
      <div
        className="calendar-date-picker-popover calendar-range-picker-popover"
        style={{
          top: `${position.top}px`,
          left: `${position.left}px`,
          width: `${position.width}px`,
        }}
        role="dialog"
        aria-modal="true"
        aria-label="Selecionar período"
      >
        <Calendar
          mode="range"
          compact
          title={title}
          subtitle={subtitle}
          initialStartDate={startValue || null}
          initialEndDate={endValue || null}
          minDate={minDate}
          maxDate={maxDate}
          onRangeSelect={handleRangeSelect}
        />
        <div className="calendar-range-picker-footer">
          {(startValue || endValue) && onClear && (
            <button type="button" className="calendar-range-picker-clear" onClick={handleClear}>
              Limpar
            </button>
          )}
          <button
            type="button"
            className="calendar-range-picker-apply"
            onClick={handleApply}
            disabled={selectSingleDay ? !pendingStart : (!pendingStart || !pendingEnd)}
          >
            Aplicar
          </button>
        </div>
      </div>
    </>
  );

  return createPortal(portalContent, document.body);
};

export default Calendar;
